import { useEffect, useState } from "react";
import SendIcon from "@mui/icons-material/Send";
import { Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText } from "@mui/material";
import Button from "@components/Button/Button";
import { z } from "zod";
import NotifyDropdown from "../components/NotifyDropdown";
import { NOTIFY_LIST } from "../preview/utils";

export interface NotifyAndSendMessageButtonProps {
  onSubmit: () => Promise<void>;
  isSendByEnterKey: boolean;
  validateMessage: () => {
    hasError: boolean;
  };
  setIsSentByEnterKey: (arg: boolean) => void;
  setRetrivedNotificationList: (arg: string[]) => void;
}

const notifySchema = z.object({
  recipients: z.array(z.string()).min(1, "Please select at least one recipient."),
  message: z.string().min(1, "Message cannot be empty"),
});

const NotifyAndSendMessageButton = ({
  onSubmit,
  isSendByEnterKey,
  validateMessage,
  setIsSentByEnterKey,
  setRetrivedNotificationList,
}: NotifyAndSendMessageButtonProps) => {
  const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
  const [option, setOption] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsNotifyDialogOpen(isSendByEnterKey);
  }, [isSendByEnterKey]);

  const handleOpenDialog = async () => {
    const { hasError } = validateMessage();
    if (hasError) return;
    setIsNotifyDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsNotifyDialogOpen(false);
    setError(null);
    setOption([]);
  };

  const handleConfirm = async () => {
    const result = notifySchema.safeParse({ recipients: option });

    if (!result.success && !option.length) {
      const msg = result.error.formErrors.fieldErrors.recipients?.[0] ?? null;
      setError(msg);
      return;
    }

    setError(null);
    await onSubmit();
    setIsSentByEnterKey(false);
    setIsNotifyDialogOpen(false);
    setOption([]);
  };

  useEffect(() => {
    setRetrivedNotificationList(option);
  }, [option, setRetrivedNotificationList]);

  return (
    <>
      <Button
        testId={`test-renew`}
        variant="outlined"
        type="button"
        className="whitespace-nowrap"
        onClick={handleOpenDialog}
        label={<span className="normal-case font-medium">Notify</span>}
      />
      <Dialog disableEscapeKeyDown open={isNotifyDialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Choose who you want to notifiy</DialogTitle>
        <DialogContent>
          <div className="w-[500px] py-6">
            <NotifyDropdown
              options={NOTIFY_LIST}
              option={option}
              setOption={(val: string[]) => {
                setOption(val);
                if (error && val.length > 0) setError(null); // clear error on selection
              }}
            />
            {error && (
              <FormHelperText
                sx={{
                  ml: 0,
                  fontWeight: 500,
                  fontSize: 12,
                  marginY: "4px",
                  height: "20px",
                  visibility: error ? "visible" : "hidden",
                  color: "error.main",
                }}
              >
                {error}
              </FormHelperText>
            )}
          </div>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            testId={`test-renew`}
            variant="text"
            type="button"
            onClick={handleCloseDialog}
            label={<span className="normal-case font-medium">Cancel</span>}
          />

          <Button
            testId={`test-renew`}
            variant="contained"
            type="button"
            endIcon={<SendIcon />}
            onClick={handleConfirm}
            label={<span className="normal-case font-medium">Notify & Send Message</span>}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotifyAndSendMessageButton;
